import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { requireLegacyWRM } from '@confluence/wrm';

import { globalRequire } from '../../tech-debt/facades/window';

/* istanbul ignore next */
const noop = () => {};

export default class Flag extends Component {
	static propTypes = {
		/**
		 * Whether to show an error flag or not
		 */
		isShowing: PropTypes.bool,
		/**
		 * What type of flag to show
		 */
		type: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
		/**
		 * The title to use in the flag
		 */
		title: PropTypes.string,
		/**
		 * The body of the flag
		 */
		body: PropTypes.string,
		/**
		 * How the flag should be closed, either auto, manual or never
		 */
		close: PropTypes.oneOf(['manual', 'auto', 'never']),
		/**
		 * A callback function which is called when flag closes
		 */
		onClose: PropTypes.func,
		/**
		 * A callback function which is called when flag shows.
		 * The callback will be passed a DOM element of flag which is created by AUIFlag function.
		 */
		onShow: PropTypes.func,
		/**
		 * Extra classname
		 */
		extraClasses: PropTypes.string,
	};

	static defaultProps = {
		onShow: noop,
		onClose: noop,
	};

	componentDidMount() {
		this._createFlag(this.props);
	}

	shouldComponentUpdate(nextProps) {
		return !isEqual(this.props, nextProps);
	}

	componentDidUpdate() {
		this._closeFlag(null, true);
		this._createFlag(this.props, true);
	}

	componentWillUnmount() {
		this._closeFlag();
	}

	_closeFlag = (event, isUpdateFlag) => {
		if (this.flagEl) {
			this.flagEl.removeEventListener('aui-flag-close', this._closeFlag);
			// `this.flagEl.close` will trigger `aui-flag-close` event so this line should be after above removing listener event.
			this.flagEl.close();
			if (this.props.onClose) {
				this.props.onClose(isUpdateFlag);
			}
			this.flagEl = undefined;
		}
	};

	_createFlag(props, isUpdateFlag) {
		const { isShowing, type, title, body, close, extraClasses, ...otherProps } = props;

		if (!isShowing) {
			return null;
		}

		requireLegacyWRM(
			['wrc!confluence.aui.staging:confluence-flags'],
			() => {
				const AUIFlag = globalRequire('confluence/flag');

				// a new flagEl can be created when props are updated.
				// so we need to close the old one and show the new one.
				this.flagEl && this._closeFlag(null, isUpdateFlag);

				try {
					this.flagEl = AUIFlag({
						type,
						title,
						body,
						close,
						extraClasses,
						...otherProps,
					});
					this.flagEl.addEventListener('aui-flag-close', this._closeFlag);

					if (this.props.onShow) {
						this.props.onShow(this.flagEl);
					}
				} catch (error) {
					console.error(error);
				}
			},
			() => {
				if (process.env.NODE_ENV !== 'production') {
					console.error(
						'Legacy ADG2 flag cannot be initialized when WRM is disabled. Please use @confluence/flag',
					);
				}
			},
		);

		return null;
	}

	render() {
		return null;
	}
}
